import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { isTenantProfile } from '@/components/Auth/checkUserPermissions';
import Header from '@/components/Header/Header.tsx';
import {
  getGetBearersForIssuedTicketQueryKey,
  getGetTicketsForUserOrganizationQueryKey,
  type IssuedTicketFindResultDTO,
  type IssuedTicketRevokeDTO,
  type TransactionDTO,
  useGetBearersForIssuedTicket,
  useGetCollectionTicketClaims,
  useGetIssuedTicketsWithTravellerByTransactionID,
  useGetTicketsForUserOrganization,
  useGetTransactionsForIssuedTicket,
  useGetTransactionWithProducts,
  useGetTravellerBearers,
  useGetTravellerByID,
  useLendIssuedTicket,
  useRevokeIssuedTicket,
  useUpdateBearerForIssuedTicket,
  useUsersListNamesForIDs,
} from '@/api/trafikportalen';
import { useVisibleTicketsUpdater } from '@/hooks/useVisibleTicketsUpdater';
import { FormattedMessage, useIntl } from 'react-intl';
import { useCallback, useMemo, useState, useEffect } from 'react';
import { IconArrowRight, IconCheck, IconEdit, IconTrash } from '@/icons';
import Button from '@/components/core/Button.tsx';
import { CenteredColumn } from '@/components/CenteredColumn.tsx';
import { CancelIssuedTicketDialog } from '@/components/Tickets/CancelIssuedTicketDialog';
import { DialogWindow } from '@/components/Dialog';
import { Loading } from '@/components/Loading';
import { DataNotFound } from '@/components/DataNotFound';
import { FormElement } from '@/components/FormElement';
import { formatDate, formatDateTime } from '@/helpers/formatting';
import { formatIssuedTicketClass } from '@/helpers/ticketClass';
import { useTenantID } from '@/hooks/useTenantID';
import { useStudentsDisabled } from '@/contexts/ConfigContext';
import { useDisplayedTicketStatus } from '@/hooks/useDisplayedTicketStatus';
import { useTagTitles } from '@/hooks/useTagTitles';
import { useTransactionTranslations } from '@/hooks/useTransactionTranslations';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/useToast.tsx';
import { useFavoredBearer } from '@/hooks/useFavoredBearer';
import { Section } from '@/components/Section';
import { BearerSelection } from '@/components/BearerSelection';
import { Bearer } from '@/components/Bearer';
import { FavoredBadge } from '@/components/FavoredBadge';
import { BearerListItem, useBearerMapping } from '@/hooks/useBearerMapping';
import { DocumentHead } from '@/components/core/DocumentHead';
import { OriginalBearerInfo } from '@/components/OriginalBearerInfo';
import { invalidateIssuedTicketQueries } from '@/helpers/issuedTicketQueries';
import { issuedTicketRevokeReasonTitle } from '@/helpers/issuedTicketRevokeReason';
import {
  canCancelTicket,
  canChangeTicketBearer,
  periodTicketCancelMessages,
} from '@/helpers/ticket';
import { usePeriodTicketCancelState } from '@/hooks/usePeriodTicketCancelState';
import {
  useGetDepartmentDisplayName,
  useStudentCategoryType,
} from '@/hooks/useGetStudentCollection';
import { STUDENT_TYPE_ROUTE } from '@/helpers/collectionTypes';
import { WarningBanner } from '@/components/core/WarningBanner';
import { TicketPeriod } from '@/components/TicketPeriod';
import { TicketActivationDetails } from '@/components/Tickets/TicketActivationDetails';
import { useAuth } from '@/stores/auth';
import { useTicketBobcatData } from '@/stores/ticketBobcatData';

export const Route = createFileRoute('/$tenantId/_layout/tickets/$ticketId/')({
  component: Ticket,
  beforeLoad: ({ params: { tenantId } }) => {
    if (isTenantProfile()) {
      throw redirect({ to: '/$tenantId', params: { tenantId } });
    }
  },
});

function Ticket() {
  const params = Route.useParams();
  const tenantId = useTenantID();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();

  const { data: tickets, isLoading } = useGetTicketsForUserOrganization();
  const ticket = tickets?.find((ticket) => ticket.id === params.ticketId);

  if (isLoading) {
    return <Loading />;
  }

  if (!ticket) {
    return (
      <DataNotFound
        description={formatMessage({
          defaultMessage: 'Biljetten hittades inte',
        })}
        buttonTitle={formatMessage({
          defaultMessage: 'Tillbaka',
        })}
        onClick={() =>
          navigate({
            to: '/$tenantId/tickets',
            params: { tenantId },
          })
        }
      />
    );
  }

  return <TicketDetails ticket={ticket} />;
}

const TicketDetails = ({ ticket }: { ticket: IssuedTicketFindResultDTO }) => {
  const { formatMessage, locale } = useIntl();
  const tenantId = useTenantID();
  const status = useDisplayedTicketStatus(ticket);
  const { userProfile } = useAuth();
  const revokeIssuedTicket = useRevokeIssuedTicket();
  const queryClient = useQueryClient();
  const getDepartmentDisplayName = useGetDepartmentDisplayName();
  const departmentName = getDepartmentDisplayName(ticket.department_id);
  const studentsDisabled = useStudentsDisabled();
  const studentCategoryType = useStudentCategoryType(ticket.department_id);
  const showStudentCategory = Boolean(studentCategoryType) && !studentsDisabled;
  const tagTitles = useTagTitles();
  const { getStateTranslation } = useTransactionTranslations();
  const ticketClassText = useMemo(
    () => formatIssuedTicketClass(ticket, tagTitles),
    [tagTitles, ticket],
  );

  const {
    created_at,
    id,
    department_id,
    traveller_first_name,
    traveller_last_name,
    traveller_email,
    traveller_id,
    bobcat_mtb_product_ids,
    bobcat_order_id,
    bobcat_purchase_transaction_id,
    recipient_name,
  } = ticket;

  const periodCancel = usePeriodTicketCancelState(ticket);
  const periodCancelDescriptionId = `period-ticket-cancel-${id}`;
  const { data: periodClaims, isFetched: periodClaimsFetched } =
    useGetCollectionTicketClaims(ticket.collection_id ?? '', {
      query: {
        enabled: Boolean(
          ticket.collection_ticket_claim_id && ticket.collection_id,
        ),
      },
    });
  const periodIsFree =
    periodClaims?.find(
      (claim) => claim.id === ticket.collection_ticket_claim_id,
    )?.non_invoiced === true;
  const periodBillingKnown =
    !ticket.collection_ticket_claim_id ||
    !ticket.collection_id ||
    periodClaimsFetched ||
    periodClaims !== undefined;

  const bobcatData = useTicketBobcatData(id);

  const updateTicketState = useVisibleTicketsUpdater({
    queryKey: getGetTicketsForUserOrganizationQueryKey(),
  });

  const { data: traveller } = useGetTravellerByID(traveller_id ?? '', {
    query: { enabled: !recipient_name && !!userProfile?.organization_id },
  });

  const { data: travellerBearers, isLoading: isLoadingTravellerBearers } =
    useGetTravellerBearers(ticket.traveller_id ?? '');
  const { data: ticketBearers, isLoading: isLoadingTicketBearers } =
    useGetBearersForIssuedTicket(ticket.id);

  const { data: ticketTransactions } = useGetTransactionsForIssuedTicket(id);

  useEffect(() => {
    if (ticket.state !== 'active' && ticket.state !== 'inactive') return;

    const timeoutId = setTimeout(() => {
      updateTicketState([ticket]);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [ticket.id, ticket.state, updateTicketState, ticket]);

  const sortedTransactions = useMemo(
    () =>
      [...(ticketTransactions ?? [])].sort((a, b) =>
        b.created_at.localeCompare(a.created_at),
      ),
    [ticketTransactions],
  );

  const refundedTransactions = useMemo(
    () =>
      sortedTransactions.filter(
        (transaction) => !!transaction.refund_transaction_id,
      ),
    [sortedTransactions],
  );

  const transactionId = useMemo(
    () =>
      [...sortedTransactions]
        .reverse()
        .find((transaction) => !transaction.refunded_transaction_id)?.id ?? '',
    [sortedTransactions],
  );

  const { data: issuedTicketsWithTraveller } =
    useGetIssuedTicketsWithTravellerByTransactionID(
      { transaction_id: transactionId },
      {
        query: { enabled: !!transactionId },
      },
    );

  const transactionTicket = useMemo(() => {
    return issuedTicketsWithTraveller?.find((t) => t.ticket.id === ticket.id)
      ?.transaction_ticket;
  }, [issuedTicketsWithTraveller, ticket.id]);

  const { mapTravellerBearers } = useBearerMapping();

  const mostRecentBobcatBearerId =
    traveller?.traveller.favored_bobcat_bearer_id;

  const canCancel = canCancelTicket(ticket);
  const canChangeBearer = !recipient_name && canChangeTicketBearer(ticket);

  const [changeBearerOpen, setChangeBearerOpen] = useState<boolean>(false);
  const [cancelOpen, setCancelOpen] = useState<boolean>(false);

  const travellerName = useMemo(() => {
    if (recipient_name) {
      return recipient_name;
    }
    if (!traveller_first_name && !traveller_last_name) {
      return 'Anonym resenär';
    }
    return `${traveller_first_name ?? ''} ${traveller_last_name ?? ''}`.trim();
  }, [traveller_first_name, traveller_last_name, recipient_name]);

  const travellerContent = useMemo(() => {
    if (!traveller_id) {
      return (
        <p className="break-words">
          {travellerName} {traveller_email ? `(${traveller_email})` : ''}
        </p>
      );
    }

    if (!traveller) {
      return (
        <p className="break-words">
          <FormattedMessage defaultMessage="Borttagen resenär" />
        </p>
      );
    }

    return (
      <Button
        to="/$tenantId/travellers/$travellerId"
        params={{
          tenantId,
          travellerId: traveller_id,
        }}
        size="sm"
        className="w-full justify-between"
        endIcon={<IconArrowRight aria-hidden="true" />}
      >
        <span className="min-w-0 truncate">
          {travellerName} {traveller_email ? `(${traveller_email})` : ''}
        </span>
      </Button>
    );
  }, [traveller_id, traveller, tenantId, travellerName, traveller_email]);

  const isUserRevoke = ticket.revoke_reason === 'revoked_by_user';
  const revokedById = ticket.revoked_by ?? '';
  const { data: revokedByUsers, isFetched: revokedByFetched } =
    useUsersListNamesForIDs(
      { ids: revokedById },
      { query: { enabled: revokedById.length > 0 } },
    );
  const revokedByName = useMemo(() => {
    const user = revokedByUsers?.find((entry) => entry.id === revokedById);
    if (!user) return undefined;
    const name = `${user.firstname} ${user.lastname}`.trim();
    return name || undefined;
  }, [revokedById, revokedByUsers]);
  const revokeRowReady = !isUserRevoke || !revokedById || revokedByFetched;
  const userRevokeLabel = useMemo(() => {
    if (!isUserRevoke || !revokeRowReady) return undefined;
    return (
      revokedByName ??
      formatMessage({
        defaultMessage: 'Okänd användare (behörighet saknas eller borttagen)',
      })
    );
  }, [formatMessage, isUserRevoke, revokeRowReady, revokedByName]);
  const revokeExplanation = useMemo(
    () => issuedTicketRevokeReasonTitle(formatMessage, ticket, userRevokeLabel),
    [formatMessage, ticket, userRevokeLabel],
  );
  const hasRevokeCause = Boolean(ticket.revoke_reason || revokedById);

  const onCancel = useCallback(() => {
    setCancelOpen(false);
  }, [setCancelOpen]);

  const onCancelConfirm = useCallback(
    (data: IssuedTicketRevokeDTO) => {
      revokeIssuedTicket.mutate(
        { id, data },
        {
          onSuccess: async () => {
            toast.success(
              {
                title: formatMessage(
                  {
                    defaultMessage:
                      'Biljetten för resenären {firstName} {lastName} avslutades',
                  },
                  {
                    firstName: traveller_first_name,
                    lastName: traveller_last_name,
                  },
                ),
              },
              { autoClose: 3000 },
            );
            await invalidateIssuedTicketQueries(queryClient, traveller_id);
            onCancel();
          },
          onError: (error) => {
            console.error('Error: ', error);
            toast.error(
              {
                title: 'Ett fel inträffade',
                description:
                  'Försök spara igen senare, eller vänligen kontakta support.',
              },
              { autoClose: 3000 },
            );
          },
        },
      );
    },
    [
      revokeIssuedTicket,
      id,
      formatMessage,
      traveller_first_name,
      traveller_last_name,
      traveller_id,
      queryClient,
      onCancel,
    ],
  );

  const bearerItems = useMemo<BearerListItem[]>(() => {
    if (!travellerBearers) {
      return [];
    }

    return mapTravellerBearers(travellerBearers);
  }, [mapTravellerBearers, travellerBearers]);

  const currentBearer = useMemo(() => {
    if (!bearerItems) return null;

    if (ticketBearers?.[0]?.mtb_bearer_id) {
      return (
        bearerItems.find(
          (bearer) => bearer.id === ticketBearers?.[0]?.mtb_bearer_id,
        ) || null
      );
    }
    return null;
  }, [bearerItems, ticketBearers]);

  const bearerContent = useMemo(() => {
    if (recipient_name) {
      return (
        <Bearer
          bearer={{
            id: 'temporary',
            type: transactionTicket?.original_bearer_type,
            name:
              transactionTicket?.original_bearer_type === 'smartphone'
                ? transactionTicket?.original_bearer || 'Smartphone'
                : '',
            serial: transactionTicket?.original_bearer,
          }}
        />
      );
    }
    if (currentBearer) {
      const isFavored = currentBearer.id === mostRecentBobcatBearerId;
      return (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <Bearer bearer={currentBearer} />
            {isFavored && <FavoredBadge />}
          </div>
          {transactionTicket && (
            <OriginalBearerInfo transactionTicket={transactionTicket} />
          )}
        </>
      );
    }
    return (
      <>
        <span className="italic text-text-700">
          <FormattedMessage defaultMessage="Okänd bärare (finns inte kopplad genom denna portalen)" />
        </span>
        {transactionTicket && (
          <OriginalBearerInfo transactionTicket={transactionTicket} />
        )}
      </>
    );
  }, [
    currentBearer,
    mostRecentBobcatBearerId,
    recipient_name,
    transactionTicket,
  ]);

  if (isLoadingTravellerBearers || isLoadingTicketBearers) {
    return <Loading />;
  }

  return (
    <div className="overflow-y-auto">
      <DocumentHead
        title={`Biljett ${id.slice(-8)}`}
        description={`Biljettdetaljer för ${travellerName} - ${ticketClassText || 'Kollektivtrafikbiljett'}`}
      />
      <Header title={formatMessage({ defaultMessage: 'Biljettdetaljer' })} />
      <CenteredColumn>
        {refundedTransactions.map((transaction) => (
          <RefundNotice key={transaction.id} transaction={transaction} />
        ))}

        <Section className="gap-2">
          <div className="mb-4 flex flex-col gap-x-2">
            <p className="text-sm text-text-700">
              <FormattedMessage defaultMessage="Internt ID:" />{' '}
              <span className="break-all font-mono">{id}</span>
            </p>
            <p className="text-sm text-text-700">
              <FormattedMessage defaultMessage="Bobcat MTB product id(s):" />{' '}
              <span className="break-all font-mono">
                {bobcat_mtb_product_ids?.join(', ')}
              </span>
            </p>
            {bobcat_order_id ? (
              <p className="text-sm text-text-700">
                <FormattedMessage defaultMessage="Bobcat order-ID:" />{' '}
                <span className="break-all font-mono">{bobcat_order_id}</span>
              </p>
            ) : null}
            {bobcat_purchase_transaction_id ? (
              <p className="text-sm text-text-700">
                <FormattedMessage defaultMessage="Bobcat transaktions-ID:" />{' '}
                <span className="break-all font-mono">
                  {bobcat_purchase_transaction_id}
                </span>
              </p>
            ) : null}
          </div>
          <FormElement
            label={formatMessage({ defaultMessage: 'Skapad:' })}
            input={<p>{formatDateTime(created_at)}</p>}
          />
          <FormElement
            label={formatMessage({ defaultMessage: 'Status:' })}
            input={
              <p className="break-words">
                {status.notice ?? (
                  <>
                    {status.label}
                    {status.description ? ` ${status.description}` : null}
                  </>
                )}
                {!hasRevokeCause && revokeExplanation ? (
                  <span className="text-text-700"> ({revokeExplanation})</span>
                ) : null}
              </p>
            }
          />
          {hasRevokeCause && revokeRowReady && revokeExplanation ? (
            <FormElement
              label={formatMessage({ defaultMessage: 'Avslutad av:' })}
              input={
                <p
                  className={
                    isUserRevoke && !revokedByName
                      ? 'break-words italic text-text-700'
                      : 'break-words'
                  }
                >
                  {revokeExplanation}
                </p>
              }
            />
          ) : null}
          <FormElement
            label={formatMessage({ defaultMessage: 'Biljett:' })}
            input={
              <p className="break-words">
                {ticketClassText ||
                  formatMessage({
                    defaultMessage: 'Ingen biljettyp tilldelad',
                  })}
              </p>
            }
          />
          <TicketActivationDetails bobcatData={bobcatData} />
          {ticket.collection_ticket_claim_id ? (
            <TicketPeriod
              claimId={ticket.collection_ticket_claim_id}
              collectionId={ticket.collection_id}
              collectionType={ticket.collection_type}
            />
          ) : null}
          {recipient_name ? (
            <FormElement
              label={formatMessage({ defaultMessage: 'Tillfällig resenär:' })}
              input={<p className="break-words">{recipient_name}</p>}
            />
          ) : (
            <FormElement
              label={formatMessage({ defaultMessage: 'Resenär:' })}
              input={travellerContent}
            />
          )}
          {showStudentCategory && studentCategoryType ? (
            <FormElement
              label={formatMessage({ defaultMessage: 'Elevkategori:' })}
              input={undefined}
              button={
                <Button
                  to={STUDENT_TYPE_ROUTE[studentCategoryType]}
                  params={{ tenantId }}
                  size="sm"
                  className="w-full justify-between"
                  endIcon={<IconArrowRight aria-hidden="true" />}
                >
                  <span className="min-w-0 truncate">{departmentName}</span>
                </Button>
              }
            />
          ) : (
            <FormElement
              label={formatMessage({ defaultMessage: 'Avdelning:' })}
              input={undefined}
              button={
                <Button
                  to="/$tenantId/departments/$departmentId"
                  params={{
                    departmentId: department_id,
                    tenantId,
                    view: 'information',
                  }}
                  size="sm"
                  className="w-full justify-between"
                  endIcon={<IconArrowRight aria-hidden="true" />}
                >
                  <span className="min-w-0 truncate">{departmentName}</span>
                </Button>
              }
            />
          )}
          {/* Disabled collection name for now as it is not yet used and it is currently difficult to know when to display it. */}
          {/* {!studentCategoryType && collection_name && (
            <FormElement
              label={formatMessage({ defaultMessage: 'Samling:' })}
              input={<p>{collection_name}</p>}
            />
          )} */}

          <FormElement
            label={formatMessage({ defaultMessage: 'Bärare:' })}
            input={<div className="flex flex-col gap-1">{bearerContent}</div>}
          />

          {sortedTransactions.length > 0 ? (
            <FormElement
              label={formatMessage(
                {
                  defaultMessage:
                    '{count, plural, one {Transaktion:} other {Transaktioner:}}',
                },
                { count: sortedTransactions.length },
              )}
              input={undefined}
              button={
                <div className="flex flex-col gap-2">
                  {sortedTransactions.map((transaction) => (
                    <Button
                      key={transaction.id}
                      to="/$tenantId/reports/transactions/$transactionId"
                      params={{
                        transactionId: transaction.id,
                        tenantId,
                      }}
                      size="sm"
                      className="w-full justify-between"
                      endIcon={<IconArrowRight aria-hidden="true" />}
                    >
                      <span className="flex min-w-0 gap-4 truncate">
                        <span>{formatDate(transaction.created_at)}</span>
                        <span>
                          <FormattedMessage
                            defaultMessage="{amount} SEK"
                            values={{
                              amount: (transaction.state === 'refunded'
                                ? -transaction.amount_inc_vat
                                : transaction.amount_inc_vat
                              ).toLocaleString(locale),
                            }}
                          />
                        </span>
                        <span>
                          {getStateTranslation({ state: transaction.state })}
                        </span>
                        <span>
                          {transaction.refund_transaction_id
                            ? ` (${formatMessage({ defaultMessage: 'Återbetald' })})`
                            : ''}
                        </span>
                      </span>
                    </Button>
                  ))}
                </div>
              }
            />
          ) : (
            <FormElement
              label={formatMessage({ defaultMessage: 'Transaktion:' })}
              input={
                <i className="text-text-700">
                  {periodIsFree ? (
                    <FormattedMessage defaultMessage="Inga transaktioner skapas för den här biljetten eftersom perioden är kostnadsfri." />
                  ) : periodBillingKnown ? (
                    <FormattedMessage defaultMessage="Transaktion skapas automatiskt vid månadsavstämning" />
                  ) : null}
                </i>
              }
            />
          )}

          {canCancel || canChangeBearer ? (
            <div className="mt-5 flex flex-col gap-2">
              <div className="ml-auto flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
                {canChangeBearer ? (
                  <Button
                    startIcon={<IconEdit />}
                    onClick={() => setChangeBearerOpen(true)}
                  >
                    <FormattedMessage defaultMessage="Byt bärare" />
                  </Button>
                ) : null}
                {canCancel ? (
                  <Button
                    startIcon={<IconTrash />}
                    variant="ghost"
                    color="red"
                    onClick={() => setCancelOpen(true)}
                    disabled={periodCancel.locked}
                    className={
                      periodCancel.locked ? 'disabled:opacity-40' : undefined
                    }
                    aria-describedby={
                      periodCancel.explain
                        ? periodCancelDescriptionId
                        : undefined
                    }
                    aria-busy={periodCancel.pending || undefined}
                  >
                    <FormattedMessage defaultMessage="Avsluta biljett" />
                  </Button>
                ) : null}
              </div>
              {canCancel && periodCancel.explain ? (
                <p
                  id={periodCancelDescriptionId}
                  className="text-sm text-text-700 sm:text-right"
                >
                  <FormattedMessage {...periodTicketCancelMessages.reason} />
                </p>
              ) : null}
            </div>
          ) : null}
        </Section>

        <DialogWindow
          title={formatMessage({
            defaultMessage: 'Byt bärare',
          })}
          content={
            <UpdateBearerDialog
              traveller_id={traveller_id ?? ''}
              bearerItems={bearerItems}
              ticket_id={id}
              currentBearer={currentBearer}
              mostRecentBobcatBearerId={mostRecentBobcatBearerId}
              onChangeBearerOpen={setChangeBearerOpen}
              isLoaned={!!ticket.loan_transaction_id}
            />
          }
          open={changeBearerOpen}
          onOpenChange={setChangeBearerOpen}
        />

        <CancelIssuedTicketDialog
          open={cancelOpen}
          description={
            <FormattedMessage defaultMessage="Är du säker på att du vill avsluta biljetten? Köpet återbetalas inte." />
          }
          loading={revokeIssuedTicket.isPending}
          onCancel={onCancel}
          onConfirm={onCancelConfirm}
        />
      </CenteredColumn>
    </div>
  );
};

interface RefundNoticeProps {
  transaction: TransactionDTO;
}

const RefundNotice = ({ transaction }: RefundNoticeProps) => {
  const tenantId = useTenantID();
  const refundTransactionId = transaction.refund_transaction_id ?? '';

  const { data: refundTransaction } = useGetTransactionWithProducts(
    refundTransactionId,
    { query: { enabled: !!refundTransactionId } },
  );

  if (!refundTransaction) return null;

  return (
    <Section className="gap-2 border-yellow-500">
      <p className="text-lg font-bold text-text-900">
        <FormattedMessage defaultMessage="Återbetalning" />
      </p>
      <p className="text-text-700">
        <FormattedMessage
          defaultMessage="Transaktionen från {date} har återbetalats"
          values={{ date: formatDateTime(transaction.created_at) }}
        />
      </p>
      <p className="text-text-900">
        <FormattedMessage defaultMessage="När:" />{' '}
        {formatDateTime(refundTransaction.transaction.created_at)}
      </p>
      <Button
        to="/$tenantId/reports/transactions/$transactionId"
        params={{
          transactionId: refundTransaction.transaction.id,
          tenantId,
        }}
        endIcon={<IconArrowRight aria-hidden="true" />}
      >
        <FormattedMessage defaultMessage="Visa återbetalning" />
      </Button>
    </Section>
  );
};

interface BearerSelectionUpdateProps {
  traveller_id: string;
  ticket_id: string;
  bearerItems: BearerListItem[];
  currentBearer: BearerListItem | null;
  mostRecentBobcatBearerId?: string;
  onChangeBearerOpen: (open: boolean) => void;
  isLoaned: boolean;
}

const UpdateBearerDialog = ({
  traveller_id,
  ticket_id,
  bearerItems,
  currentBearer,
  mostRecentBobcatBearerId,
  onChangeBearerOpen,
  isLoaned,
}: BearerSelectionUpdateProps) => {
  const { formatMessage } = useIntl();
  const [selectedBearerId, setSelectedBearerId] = useState<string>('');
  const queryClient = useQueryClient();

  const moveTicket = useUpdateBearerForIssuedTicket();
  const lendTicket = useLendIssuedTicket();
  const { setFavoredBearer, getFavoredBearerErrorMessage, isPending } =
    useFavoredBearer({ travellerId: traveller_id });

  const mutation = isLoaned ? lendTicket : moveTicket;

  const handleBearerSelect = useCallback(
    (bearerId: string) => {
      setSelectedBearerId(bearerId);
    },
    [setSelectedBearerId],
  );

  const cancelUpdate = useCallback(() => {
    setSelectedBearerId('');
    onChangeBearerOpen(false);
  }, [setSelectedBearerId, onChangeBearerOpen]);

  const onUpdateBearer = useCallback(async () => {
    if (selectedBearerId.length === 0) return;

    await mutation.mutateAsync(
      {
        ticketID: ticket_id,
        data: {
          mtb_bearer_id: selectedBearerId,
        },
      },
      {
        onSuccess: async () => {
          let favoredBearerUpdated = true;

          if (traveller_id && selectedBearerId !== mostRecentBobcatBearerId) {
            try {
              await setFavoredBearer(selectedBearerId, { showToast: false });
            } catch (error) {
              favoredBearerUpdated = false;
              toast.error(
                {
                  title: formatMessage({
                    defaultMessage: 'Kunde inte uppdatera föredragen bärare',
                  }),
                  description: getFavoredBearerErrorMessage(error),
                },
                { autoClose: 3000 },
              );
            }
          }

          if (favoredBearerUpdated) {
            toast.success(
              {
                title: `Bärare uppdaterad.`,
              },
              { autoClose: 4000 },
            );
          }
          await queryClient.invalidateQueries({
            queryKey: getGetBearersForIssuedTicketQueryKey(ticket_id),
          });
          setSelectedBearerId('');
          onChangeBearerOpen(false);
        },
        onError: (error) => {
          console.error('Error: ', error);
          toast.error(
            {
              title: 'Ett fel inträffade',
              description:
                'Försök spara igen senare, eller vänligen kontakta support.',
            },
            { autoClose: 3000 },
          );
        },
      },
    );
  }, [
    selectedBearerId,
    ticket_id,
    mutation,
    queryClient,
    onChangeBearerOpen,
    traveller_id,
    mostRecentBobcatBearerId,
    setFavoredBearer,
    formatMessage,
    getFavoredBearerErrorMessage,
  ]);

  useEffect(() => {
    if (selectedBearerId.length === 0 && currentBearer) {
      setSelectedBearerId(currentBearer.id);
    }
  }, [selectedBearerId, currentBearer]);

  return (
    <>
      <WarningBanner className="mb-4">
        <FormattedMessage
          id="page.traveller.changeBearerWarning"
          defaultMessage="När man byter bärare ändras vilken bärare som är Föredragen. Alla automatiskt utställda biljetter (t.ex. skolbiljetter) kommer automatiskt att hamna på den nya Föredragna bäraren framöver."
        />
      </WarningBanner>
      {traveller_id && (
        <BearerSelection
          bearers={bearerItems}
          onBearerSelect={handleBearerSelect}
          selectedBearer={selectedBearerId}
          mostRecentBobcatBearerId={mostRecentBobcatBearerId}
        />
      )}
      <div className="mt-4 flex w-full flex-wrap justify-end gap-2">
        <Button onClick={cancelUpdate} variant="ghost">
          <FormattedMessage defaultMessage="Avbryt" />
        </Button>
        <Button
          startIcon={<IconCheck />}
          variant="filled"
          type="submit"
          disabled={selectedBearerId === currentBearer?.id}
          loading={mutation.isPending || isPending}
          onClick={onUpdateBearer}
        >
          <FormattedMessage defaultMessage="Byt bärare" />
        </Button>
      </div>
    </>
  );
};
